import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api, { formatApiError } from "@/api/client";
import { Loader2 } from "lucide-react";

export default function Register() {
    const navigate = useNavigate();
    const { register } = useAuth();
    const [form, setForm] = useState({
        firm_name: "",
        admin_name: "",
        email: "",
        password: "",
    });
    const [busy, setBusy] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const { data } = await api.post("/auth/register", {
                ...form,
                email: form.email.trim().toLowerCase(),
            });
            setSubmitted(true);
            toast.success(data.message || "Submitted for approval");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
            <div className="hidden lg:flex flex-col justify-between p-12 bg-primary text-white">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-white/15 grid place-items-center font-display font-bold text-lg">
                        C
                    </div>
                    <div className="font-display text-xl font-bold">CAFlow</div>
                </div>
                <div>
                    <h2 className="font-display text-4xl font-bold leading-tight max-w-md">
                        Set up your firm's workspace in under a minute.
                    </h2>
                    <p className="mt-4 text-white/80 max-w-md">
                        Free to start · Unlimited demo clients · No credit card required.
                    </p>
                </div>
                <div className="text-xs text-white/60">
                    Your data stays isolated to your firm at all times.
                </div>
            </div>

            <div className="flex items-center justify-center p-6 sm:p-12">
                {submitted ? (
                    <div className="w-full max-w-md text-center space-y-4" data-testid="register-submitted">
                        <div className="w-14 h-14 mx-auto rounded-full bg-emerald-50 grid place-items-center border border-emerald-100">
                            <svg viewBox="0 0 24 24" fill="none" className="w-7 h-7 text-emerald-600" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                        </div>
                        <h1 className="font-display text-2xl font-bold text-slate-900">
                            Registration submitted
                        </h1>
                        <p className="text-sm text-slate-600 max-w-sm mx-auto">
                            Your registration has been submitted successfully. Your account will be activated
                            after approval by the Platform Administrator. You'll receive an email once approved.
                        </p>
                        <Link to="/login" className="inline-block text-sm text-primary font-medium hover:underline">
                            Back to sign in
                        </Link>
                    </div>
                ) : (
                    <form onSubmit={submit} className="w-full max-w-md space-y-4" data-testid="register-form">
                    <h1 className="font-display text-3xl font-bold text-slate-900">
                        Create your firm
                    </h1>
                    <p className="text-sm text-slate-500">
                        You'll be the firm admin. Add employees after signup.
                    </p>
                    <div className="space-y-1.5">
                        <Label>Firm name</Label>
                        <Input
                            value={form.firm_name}
                            onChange={set("firm_name")}
                            placeholder="Sharma & Associates"
                            required
                            data-testid="reg-firm"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label>Your name</Label>
                        <Input
                            value={form.admin_name}
                            onChange={set("admin_name")}
                            placeholder="CA Rohan Sharma"
                            required
                            data-testid="reg-name"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label>Email</Label>
                        <Input
                            type="email"
                            value={form.email}
                            onChange={set("email")}
                            required
                            data-testid="reg-email"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label>Password</Label>
                        <Input
                            type="password"
                            value={form.password}
                            onChange={set("password")}
                            required
                            minLength={6}
                            data-testid="reg-password"
                        />
                    </div>
                    <Button
                        type="submit"
                        className="w-full bg-primary hover:bg-primary/90"
                        disabled={busy}
                        data-testid="reg-submit"
                    >
                        {busy ? <Loader2 className="animate-spin" size={16} /> : "Create firm"}
                    </Button>
                    <div className="text-sm text-slate-500 text-center">
                        Already have an account?{" "}
                        <Link to="/login" className="text-primary font-medium hover:underline">
                            Sign in
                        </Link>
                    </div>
                </form>
                )}
            </div>
        </div>
    );
}
